// packages/sync/src/parser.ts
import yaml from 'js-yaml';
const L3_REPO_TREE_BASE = 'https://github.com/Loop3Studio/l3-os/tree/main/';
function categoryFor(source) {
    if (source.startsWith('.ops/workflows/'))
        return 'workflows';
    if (source.startsWith('.ops/skills/') || source.startsWith('.agents/skills/'))
        return 'skills';
    if (source.startsWith('.ops/configs/'))
        return 'configs';
    return null;
}
function deriveOrigin(source) {
    if (source.startsWith('.ops/skills/'))
        return 'l3';
    if (source.startsWith('.agents/skills/'))
        return 'community';
    return null;
}
function deriveSourceRepo(id, origin, skillsLock) {
    if (origin !== 'community')
        return null;
    const lockEntry = skillsLock?.skills?.[id];
    if (!lockEntry)
        return null;
    if (lockEntry.sourceType !== 'github')
        return null;
    return lockEntry.source;
}
function deriveReferenceUrl(id, source, origin, skillsLock) {
    if (origin === 'l3') {
        // Trim trailing /SKILL.md so the URL points at the skill folder, not the
        // single markdown file. L3 skills are always file-sourced today; if that
        // ever changes, the slice below still yields a valid folder URL.
        const folder = source.endsWith('/SKILL.md') ? source.slice(0, -'/SKILL.md'.length) : source;
        return `${L3_REPO_TREE_BASE}${folder}`;
    }
    if (origin === 'community') {
        const lockEntry = skillsLock?.skills?.[id];
        if (!lockEntry)
            return null;
        if (lockEntry.sourceType !== 'github')
            return null;
        // skills.sh hosts a per-skill registry page (install command, readme,
        // upstream repo link) at /<source>/<id>. It's a friendlier landing than
        // the bare GitHub repo for users who just want to see what a skill is.
        return `https://skills.sh/${lockEntry.source}/${id}`;
    }
    return null;
}
export function parseSyncYml(raw, skillsLock) {
    const doc = (yaml.load(raw) ?? {});
    // managed_files
    const managedFiles = {
        workflows: [],
        skills: [],
        configs: [],
    };
    for (const [id, entry] of Object.entries(doc.managed_files ?? {})) {
        const cat = categoryFor(entry.source);
        if (!cat)
            continue;
        const origin = deriveOrigin(entry.source);
        managedFiles[cat].push({
            id,
            source: entry.source,
            dest: entry.dest,
            scope: entry.scope === 'optional' ? 'optional' : 'global',
            origin,
            referenceUrl: deriveReferenceUrl(id, entry.source, origin, skillsLock),
            sourceRepo: deriveSourceRepo(id, origin, skillsLock),
        });
    }
    // Sort: origin first (l3 before community — the Skills page groups them),
    // then globals before optional, then alphabetical. For non-skills, both
    // origins are null so the first branch is a no-op and the scope + id
    // fallback decides order — identical to the previous behavior.
    for (const k of ['workflows', 'skills', 'configs']) {
        managedFiles[k].sort((a, b) => {
            if (a.origin !== b.origin) {
                if (a.origin === 'l3')
                    return -1;
                if (b.origin === 'l3')
                    return 1;
            }
            if (a.scope !== b.scope)
                return a.scope === 'global' ? -1 : 1;
            return a.id.localeCompare(b.id);
        });
    }
    // repos
    const repos = [];
    for (const r of doc.repos ?? []) {
        if (!r.client) {
            throw new Error(`sync.yml parse: repo "${r.repo}" is missing \`client:\` field — every repo must declare its client prefix (single string or array)`);
        }
        const [org, name] = r.repo.split('/');
        const entry = {
            repo: r.repo,
            org,
            name,
            client: r.client,
            description: r.description ?? '',
            keywords: r.keywords ?? [],
            linear: { team: r.linear?.team ?? '' },
        };
        if (r.include)
            entry.include = r.include;
        if (r.token_env)
            entry.tokenEnv = r.token_env;
        if (r.sync === false)
            entry.sync = false;
        repos.push(entry);
    }
    // rulesets (pass-through, typed loosely for now)
    const rulesets = (doc.rulesets ?? []);
    // team
    const team = doc.team ?? [];
    return { managedFiles, repos, rulesets, team };
}
export function reposByClient(repos) {
    const map = {};
    for (const r of repos) {
        const prefixes = Array.isArray(r.client) ? r.client : [r.client];
        for (const p of prefixes) {
            if (!map[p])
                map[p] = [];
            map[p].push(r);
        }
    }
    return map;
}
export function reposForClient(repos, prefix) {
    return repos.filter((r) => Array.isArray(r.client) ? r.client.includes(prefix) : r.client === prefix);
}
